import React from 'react';
import { Box, Button, Stack, Typography, keyframes } from '@mui/material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ScheduleSendRoundedIcon from '@mui/icons-material/ScheduleSendRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { PaymentMethod } from '@prisma/client';
import { Form, Formik, useFormikContext } from 'formik';
import { toast } from 'react-toastify';
import BrushRoundedIcon from '@mui/icons-material/BrushRounded';
import {
  AcceptRequestFlowValues,
  acceptRequestFlowSchema,
  SchedulingMode,
} from '../../../types/appointments';
import {
  getAcceptRequestInitialValues,
  getRequestService,
  hasFlashDesign,
} from '../../../utils/form/acceptRequestPrefill';
import {
  markDepositPaid,
  submitAcceptRequest,
} from '../../../services/appointments';
import { Appointment, GeneralRequest, LoadedUser } from '../../../interfaces';
import useUser from '../../../hooks/global/useUser';
import useConfirm from '../../../hooks/useConfirm';
import useCashDiscount from '../../../hooks/payments/useCashDiscount';
import useCustomerScheduleBlockWarning from '../../../hooks/customers/useCustomerScheduleBlockWarning';
import CustomerScheduleBlockAlert from '../../customers/CustomerScheduleBlockAlert';
import {
  ImageItem,
  getExistingImgList,
} from '../../../utils/form/appointmentForm';
import TattooDetails from '../TattooDetails';
import AcceptRequestFields from './AcceptRequestFields';
import SchedulingChoice from './SchedulingChoice';
import CollapsibleSection from './CollapsibleSection';
import MultiSessionOptions from './MultiSessionOptions';
import MoreOptions from './MoreOptions';

interface Props {
  request: Appointment | GeneralRequest | undefined;
  refreshData: (
    request: Appointment | GeneralRequest,
    action?: 'accept' | 'reject',
  ) => void | Promise<void>;
  closeModals: () => void;
  onScheduled?: (appointmentId: string) => void;
  // Lets the parent modal ask for confirmation before discarding edits
  registerDirtyCheck?: (isDirty: () => boolean) => void;
}

interface SubmittedState {
  mode: AcceptRequestFlowValues['schedulingMode'];
  appointmentId?: string;
  depositPaidError?: boolean;
  depositPaymentMethod?: PaymentMethod;
  // Booking-link copy mentions deposit only when one was set
  hasDeposit?: boolean;
}

const discPop = keyframes`
  0%   { transform: scale(0.4); opacity: 0; }
  60%  { transform: scale(1.06); opacity: 1; }
  100% { transform: scale(1); opacity: 1; }
`;

const discRing = keyframes`
  0%   { transform: scale(0.6); opacity: 0.8; }
  100% { transform: scale(1.6); opacity: 0; }
`;

// base and light share the same metrics, so the pair matches by construction
const actionButtonSx = { textTransform: 'none', px: 3 } as const;

const getSubmittedCopy = (
  mode: SchedulingMode,
  clientFirstName: string,
  hasDeposit: boolean,
) => {
  if (mode === 'PICK_TIME') {
    return {
      eyebrow: 'Confirmed',
      title: 'Appointment scheduled',
      detail: "It's on your calendar.",
    };
  }

  if (mode === 'OFFER_TIMES') {
    return {
      eyebrow: 'Waiting on client',
      title: 'Times sent',
      detail: `We texted ${clientFirstName} the times you picked${
        hasDeposit ? ' and a link to pay the deposit' : ''
      }. You'll see it on your calendar once they choose one.`,
    };
  }

  return {
    eyebrow: 'Waiting on client',
    title: 'Booking link sent',
    detail: `We texted ${clientFirstName} a link to pick a time${
      hasDeposit ? ' and pay the deposit' : ''
    }. You'll see it on your calendar once they book.`,
  };
};

const getSubmitLabel = (values: AcceptRequestFlowValues) => {
  if (values.schedulingMode === 'BOOKING_LINK') return 'Send booking link';
  if (values.schedulingMode === 'OFFER_TIMES') return 'Send times';
  return values.requestKind === 'CONSULTATION'
    ? 'Schedule consultation'
    : 'Schedule appointment';
};

function FormBody({
  clientFirstName,
  customerId,
  isFlash,
  hasService,
  registerDurationValidator,
  registerDirtyCheck,
  formError,
  images,
  setImages,
}: {
  clientFirstName: string;
  customerId: string;
  isFlash: boolean;
  hasService: boolean;
  registerDurationValidator: (validator: () => Promise<boolean>) => void;
  registerDirtyCheck?: (isDirty: () => boolean) => void;
  formError: string;
  images: ImageItem[];
  setImages: React.Dispatch<React.SetStateAction<ImageItem[]>>;
}) {
  const { user: loadedUser } = useUser<LoadedUser>();
  const { values, isSubmitting, dirty } =
    useFormikContext<AcceptRequestFlowValues>();

  const { warning: scheduleBlockWarning } = useCustomerScheduleBlockWarning({
    customerId,
    businessId: values.businessId || loadedUser?.business.id,
    employeeId: values.employeeId || null,
  });

  React.useEffect(() => {
    registerDirtyCheck?.(() => dirty);
  }, [dirty, registerDirtyCheck]);

  return (
    <Form>
      {/* Pulls the alert back in from the form's 40px gap */}
      <CustomerScheduleBlockAlert warning={scheduleBlockWarning} mb={-3} />
      <AcceptRequestFields
        hasFlashDesign={isFlash}
        hasService={hasService}
        registerDurationValidator={registerDurationValidator}
      />
      <SchedulingChoice clientFirstName={clientFirstName} />
      <Stack gap={2.5} width="100%">
        <MultiSessionOptions clientFirstName={clientFirstName} />
        <CollapsibleSection
          title={hasService ? 'Service Details' : 'Tattoo Details'}
          subtitle="Location, size, color & reference images"
          icon={<BrushRoundedIcon sx={{ color: 'secondary.main' }} />}>
          <TattooDetails
            images={images}
            setImages={setImages}
            showTitle={false}
          />
        </CollapsibleSection>
        <MoreOptions />
      </Stack>
      {formError && (
        <Typography variant="body1" color="error.dark" mb={2}>
          {formError}
        </Typography>
      )}
      <Button
        type="submit"
        disabled={isSubmitting}
        variant="contained"
        className="primary">
        {getSubmitLabel(values)}
      </Button>
    </Form>
  );
}

function AcceptRequestFlow({
  request,
  refreshData,
  closeModals,
  onScheduled,
  registerDirtyCheck,
}: Props) {
  const { user: loadedUser } = useUser<LoadedUser>();
  const { cashDiscountEnabled } = useCashDiscount();
  const confirm = useConfirm();

  const [formError, setFormError] = React.useState('');
  const [submitted, setSubmitted] = React.useState<SubmittedState | null>(null);
  const [images, setImages] = React.useState<ImageItem[]>(() =>
    request ? getExistingImgList(request) : [],
  );
  const durationValidatorRef = React.useRef<(() => Promise<boolean>) | null>(
    null,
  );
  const registerDurationValidator = React.useCallback(
    (validator: () => Promise<boolean>) => {
      durationValidatorRef.current = validator;
    },
    [],
  );

  const initialValues = React.useMemo(
    () =>
      request && loadedUser
        ? getAcceptRequestInitialValues({
            request,
            loadedUser,
            cashDiscountEnabled,
          })
        : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [request, loadedUser],
  );

  if (!request || !initialValues || !loadedUser) return null;

  const clientFirstName = request.customer?.firstName || 'your client';
  const isFlash = hasFlashDesign(request);
  const service = getRequestService(request, loadedUser);

  const confirmDepositOverPrice = async (values: AcceptRequestFlowValues) => {
    if (values.requestKind === 'CONSULTATION') return true;
    const deposit = values.depositPrice || 0;
    const comparePrice =
      values.priceType === 'FIXED'
        ? values.price || 0
        : values.priceType === 'RANGE'
        ? values.minPrice || 0
        : undefined;
    if (comparePrice === undefined || deposit <= comparePrice) return true;
    return confirm({
      dismissible: true,
      title: 'Deposit is more than the price',
      message: `The deposit ($${deposit}) is more than the ${
        values.priceType === 'RANGE' ? 'minimum price' : 'price'
      } ($${comparePrice}). Are you sure you want to continue?`,
      positiveText: 'Continue',
      negativeText: 'Go back',
    });
  };

  const onSubmit = async (values: AcceptRequestFlowValues) => {
    setFormError('');

    const durationOk = await durationValidatorRef.current?.();
    if (durationOk === false) return;

    if (!(await confirmDepositOverPrice(values))) return;

    let result;
    try {
      result = await submitAcceptRequest(values, request, images);
    } catch (error) {
      const message =
        error instanceof Error && error.message
          ? error.message
          : 'Error accepting the request. Please try again.';
      setFormError(message);
      toast.error(message);
      return;
    }

    await refreshData(request, 'accept');

    const appointmentId =
      result?.appointmentId ||
      ('appointmentId' in request ? request.appointmentId : undefined);

    const depositPaymentMethod = values.depositPaymentMethod || 'CASH';
    let depositPaidError = false;
    // Consultations zero out the deposit in the payload, so a stale
    // depositPaid from before a booking-type switch must not mark one paid
    if (
      values.requestKind !== 'CONSULTATION' &&
      values.depositPaid &&
      (values.depositPrice ?? 0) > 0 &&
      appointmentId
    ) {
      try {
        await markDepositPaid(appointmentId, { depositPaymentMethod });
      } catch {
        depositPaidError = true;
      }
    }

    // The form is gone once the summary shows, so there is nothing left to
    // discard — otherwise closing the modal would still prompt about edits
    registerDirtyCheck?.(() => false);

    setSubmitted({
      mode: values.schedulingMode,
      appointmentId,
      depositPaidError,
      depositPaymentMethod,
      hasDeposit:
        values.requestKind !== 'CONSULTATION' && (values.depositPrice ?? 0) > 0,
    });
  };

  const retryMarkDepositPaid = async () => {
    if (!submitted?.appointmentId) return;
    try {
      await markDepositPaid(submitted.appointmentId, {
        depositPaymentMethod: submitted.depositPaymentMethod || 'CASH',
      });
      toast.success('Deposit recorded as paid');
      setSubmitted({ ...submitted, depositPaidError: false });
    } catch {
      toast.error("Still couldn't record the deposit. Please try again.");
    }
  };

  if (submitted) {
    const isLink = submitted.mode !== 'PICK_TIME';
    const { eyebrow, title, detail } = getSubmittedCopy(
      submitted.mode,
      clientFirstName,
      !!submitted.hasDeposit,
    );

    return (
      // Stacks, not Boxes: modal.css forces MuiBox-root to 100% on mobile,
      // which would stretch the disc out of its circle
      <Stack alignItems="center" gap={3} py={{ xs: 4, md: 6 }} px={2}>
        <Stack
          alignItems="center"
          justifyContent="center"
          sx={{
            position: 'relative',
            width: 88,
            height: 88,
            flexShrink: 0,
            borderRadius: '50%',
            // Solid green reads "done"; mint reads "sent, but not finished" —
            // a booking link still needs the client to act
            backgroundColor: isLink ? 'accent.main' : 'secondary.main',
            color: isLink ? 'secondary.main' : '#FFFFFF',
            boxShadow: `0 20px 40px -12px rgba(0, 168, 107, ${
              isLink ? 0.25 : 0.5
            }), 0 0 0 12px rgba(206, 238, 227, 0.6)`,
            animation: `${discPop} 380ms cubic-bezier(0.2, 0.9, 0.3, 1.2)`,
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: -14,
              borderRadius: '50%',
              border: '1px solid rgba(0, 168, 107, 0.25)',
              animation: `${discRing} 900ms ease-out forwards`,
            },
            '@media (prefers-reduced-motion: reduce)': {
              animation: 'none',
              '&::before': { animation: 'none', opacity: 0 },
            },
          }}>
          {isLink ? (
            <ScheduleSendRoundedIcon sx={{ fontSize: 40 }} />
          ) : (
            <CheckRoundedIcon sx={{ fontSize: 48 }} />
          )}
        </Stack>

        <Stack alignItems="center" gap={1}>
          <Typography variant="overline" sx={{ color: 'secondary.main' }}>
            {eyebrow}
          </Typography>
          <Typography variant="h2" sx={{ textAlign: 'center' }}>
            {title}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              textAlign: 'center',
              maxWidth: 420,
            }}>
            {detail}
          </Typography>
        </Stack>

        {submitted.depositPaidError && (
          <Stack
            direction="row"
            gap={1.5}
            sx={{
              width: '100%',
              maxWidth: 420,
              p: 2,
              borderRadius: '12px',
              backgroundColor: 'warning.light',
              border: theme => `1px solid ${theme.palette.warning.main}`,
            }}>
            <WarningAmberRoundedIcon
              sx={{ color: 'warning.dark', fontSize: 20, flexShrink: 0 }}
            />
            <Stack alignItems="flex-start" gap={1}>
              <Typography variant="h5" sx={{ color: 'warning.dark' }}>
                Deposit not recorded
              </Typography>
              <Typography variant="body2" sx={{ color: 'warning.dark' }}>
                The{' '}
                {isLink ? 'request was accepted' : 'appointment was scheduled'},
                but we couldn&apos;t mark the deposit as paid.
              </Typography>
              <Button
                variant="light"
                sx={{ textTransform: 'none' }}
                onClick={retryMarkDepositPaid}>
                Retry deposit
              </Button>
            </Stack>
          </Stack>
        )}

        <Stack
          direction={{ xs: 'column-reverse', sm: 'row' }}
          gap={1.5}
          justifyContent="center"
          sx={{ width: '100%', maxWidth: 420 }}>
          {!isLink && submitted.appointmentId && (
            <Button
              variant="light"
              sx={actionButtonSx}
              onClick={() => {
                closeModals();
                onScheduled?.(submitted.appointmentId!);
              }}>
              View on calendar
            </Button>
          )}
          <Button variant="base" sx={actionButtonSx} onClick={closeModals}>
            Done
          </Button>
        </Stack>
      </Stack>
    );
  }

  return (
    <Box className="accept-request-flow">
      <Formik
        initialValues={initialValues}
        validationSchema={acceptRequestFlowSchema}
        onSubmit={async (values, { setSubmitting }) => {
          await onSubmit(values);
          setSubmitting(false);
        }}>
        <FormBody
          clientFirstName={clientFirstName}
          customerId={request.customerId}
          isFlash={isFlash}
          hasService={!!service}
          registerDurationValidator={registerDurationValidator}
          registerDirtyCheck={registerDirtyCheck}
          formError={formError}
          images={images}
          setImages={setImages}
        />
      </Formik>
    </Box>
  );
}

export default AcceptRequestFlow;
